export const EMAIL_REGEX = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

/** Special characters accepted by Cognito and the password UI checklist (includes + and =). */
export const PASSWORD_SPECIAL_CHAR_REGEX = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

/** Cognito password policy: upper, lower, digit, special, 8–99 chars, no spaces. */
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~])\S{8,99}$/;

/**
 * Validates email address format.
 */
export const isValidEmail = (email: string): boolean => EMAIL_REGEX.test(email.trim());

/**
 * Validates password against Cognito policy.
 */
export const isValidPassword = (password: string): boolean => PASSWORD_REGEX.test(password);

/**
 * Checks that a password contains at least one accepted special character.
 */
export const hasPasswordSpecialCharacter = (password: string): boolean => PASSWORD_SPECIAL_CHAR_REGEX.test(password);

/**
 * Validates IFSC (Indian Financial System Code) format
 * IFSC format: 4 letters (bank code) + 0 + 6 alphanumeric characters = 11 characters total
 * @param ifsc - The IFSC code string to validate
 * @returns true if the IFSC code is valid, false otherwise
 */
export const isValidIFSC = (ifsc: string): boolean => {
  return /^[A-Z]{4}[0][A-Z0-9]{6}$/.test(ifsc);
};

/**
 * Validates SWIFT/BIC (Bank Identifier Code) format
 * SWIFT format: 4 letters (bank code) + 2 letters (country code) + 2 alphanumeric characters (location) + optional 3 characters (branch)
 * @param swiftCode - The SWIFT code string to validate
 * @returns true if the SWIFT code is valid, false otherwise
 */
export const isValidSwiftCode = (swiftCode: string): boolean => {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swiftCode);
};

/**
 * Validates UPI (Unified Payments Interface) ID format
 * UPI format: username@bankname or phone@bankname
 * @param upi - The UPI ID string to validate
 * @returns true if the UPI ID is valid, false otherwise
 */
export const isValidUPI = (upi: string): boolean => {
  if (!upi || upi.length === 0) {
    return false;
  }
  return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/.test(upi);
};

/**
 * Validates Bank Account Number
 * Bank account numbers typically range from 9 to 18 digits
 * @param accountNumber - The account number (string or number) to validate
 * @returns true if the account number is valid (9-18 digits), false otherwise
 */
export const isValidBankAccountNumber = (accountNumber: string | number): boolean => {
  // Convert to string and remove any whitespace
  const cleaned = String(accountNumber).replace(/\s/g, '');
  return /^[0-9]{9,18}$/.test(cleaned);
};

/**
 * Validates Bank Name
 * Allows letters, space, dot and comma. No leading space. No trailing space/dot/comma.
 */
export const isValidBankName = (name: string): boolean => {
  // Require at least one letter
  return /^(?=.*[A-Za-z])(?! )[A-Za-z. ,]+(?<![ .,])$/.test(name);
};

/**
 * Validates Bank Branch Name
 * Allows letters, digits, space, hyphen, slash, comma and dot. No leading space. No trailing space/hyphen/slash/comma/dot.
 */
export const isValidBankBranchName = (name: string): boolean => {
  // Require at least one letter (disallow purely numeric names)
  return /^(?=.*[A-Za-z])(?! )[A-Za-z0-9\-\/ ,.]+(?<![ \-\/,.])$/.test(name);
};

/**
 * Validates GSTIN (Goods and Services Tax Identification Number) format
 * Note: Expects input to be already trimmed and uppercased
 * @param gstin - The GSTIN string to validate (should be trimmed and uppercased)
 * @returns true if the GSTIN is valid, false otherwise
 */
export const isValidGSTIN = (gstin: string): boolean => {
  if (!gstin || gstin.length === 0) {
    return false;
  }
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z\d]{2}[1-9A-Z]{1}[0-9A-Z]{1}$/.test(gstin);
};

/**
 * Validates Bank Account Holder Name
 * Allows letters, numbers, spaces, and special characters (-, &, .)
 * Requires at least 3 characters with at least 1 alphabet
 * Empty strings are also acceptable
 * No leading or trailing spaces
 */
export const isValidAccountHolderName = (name: string): boolean => {
  // Allow empty string
  if (!name || name.length === 0) {
    return true;
  }

  // Require at least 3 characters, at least one letter, no leading/trailing spaces
  // Allowed characters: letters, numbers, spaces, -, &, ., '
  return /^(?=.*[A-Za-z])(?! )[A-Za-z0-9\-&.' ]{3,}(?<! )$/.test(name);
};

/**
 * Validates Indian PAN (Permanent Account Number) format.
 * Pattern: 5 uppercase letters + 4 digits + 1 uppercase letter.
 */
export const isValidPAN = (pan: string): boolean => {
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);
};

/**
 * Checks that a legal name contains at least one alphabet character.
 */
export const hasAlphabetCharacter = (value: string): boolean => {
  return /[a-zA-Z]/.test(value);
};

// Voucher validation regex patterns
export const VOUCHER_REGEX = {
  FORBIDDEN: /["*^%={}\[\]\\`~<>!?]|[\p{Sc}]|[\p{Emoji_Presentation}\p{Extended_Pictographic}]|[^\p{ASCII}]/u,
  START_FORBIDDEN: /^[0\/-]/,
  HAS_NUMBER: /\d/,
};

/**
 * Validates voucher number format.
 * Returns { valid: true, message: '' } if valid, or { valid: false, message: string } with the validation error message.
 */
export function validateVoucher(value: string, label = 'Voucher number'): { valid: boolean; message: string } {
  const v = value ?? '';
  const trimmed = v.trim();

  if (trimmed === '') {
    return { valid: false, message: `${label} is required` };
  }
  if (VOUCHER_REGEX.FORBIDDEN.test(v)) {
    return { valid: false, message: `${label} contains invalid characters` };
  }
  if (VOUCHER_REGEX.START_FORBIDDEN.test(v)) {
    return { valid: false, message: `${label} cannot start with 0, /, or -` };
  }
  if (!VOUCHER_REGEX.HAS_NUMBER.test(v)) {
    return { valid: false, message: `${label} must contain at least one digit` };
  }
  return { valid: true, message: '' };
}
